/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import CheckMarkFormField from '../../../components/form/CheckMarkFormField';
import CompanyFormField from '../../../components/form/Company/CompanyFormField';
import CompanyTypeFormField from '../../../components/form/Company/CompanyTypeFormField';
import SelectValueFormField from '../../../components/form/SelectValueFormField';
import c from '../../../constant';
import useStore from '../../../store';
import { mappingKhmerNumber } from '../../../utils/market-development';
import { getPermittedCompanyTypes, getPermittedReportTypes } from '../Common/Utils';
import {
	annuallyFileReport,
	mdMonthlyClaim,
	mdMonthlyDistribution,
	mdMonthlySale,
	mdQuarterlyClaim,
	mdQuarterlySale,
	monthlyFileReport,
	periodMapping,
	quarterlyFileReport,
	sdQuarterlyFinance,
	sdQuarterlyInvestment,
	semesterlyFileReport,
} from '../Common/Constants';
import CompanyReportTypeFormField from '../../../components/form/CompanyReportTypeFormField';

function Filter({
	reportType = null,
	companyId = null,
	companyType = null,
	type = null,
	month = null,
	quarter = null,
	fileReportId = null,
	year = null,
	active = false,
	onClose = () => null,
	onApply = () => null,
}) {
	const { t } = useTranslation();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const { currentLang: lang } = useStore((state) => state?.currentLang);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				reportType: Yup.string(),
				companyType: Yup.string(),
				company: Yup.object().nullable(),
				year: Yup.string(),
				month: Yup.string(),
				quarter: Yup.string(),
				fileReportId: Yup.string(),
				active: Yup.boolean(),
			}),
		[],
	);

	const reports = useMemo(() => {
		const resultReportType = getPermittedReportTypes(user);
		const mdReportTypes = resultReportType['md'] ?? [];
		const sdReportTypes = resultReportType['sd'] ?? [];
		const fileReportTypes = resultReportType['file'] ?? [];
		const localReports = [...mdReportTypes, ...sdReportTypes, ...fileReportTypes];
		return localReports?.map((localReport) => ({ label: lang === 'kh' ? localReport?.labelKh : localReport?.labelEn, value: localReport?.value }));
	}, [user, lang]);

	const getCompanyTypes = (user, reportValue) => {
		const resultCompanyTypes = getPermittedCompanyTypes(user);
		const resultReportTypes = getPermittedReportTypes(user);
		const mdCompanyTypes = resultCompanyTypes['md'] ?? [];
		const sdCompanyTypes = resultCompanyTypes['sd'] ?? [];
		const fileCompanyTypes = resultCompanyTypes['file'] ?? [];
		const mdReportTypes = resultReportTypes['md'] ?? [];
		const sdReportTypes = resultReportTypes['sd'] ?? [];
		const fileReportTypes = resultReportTypes['file'] ?? [];
		if (mdReportTypes?.some((type) => type?.value === reportValue)) {
			return c.insuranceCompanyTypes.filter((insuranceCompanyType) => mdCompanyTypes?.includes(insuranceCompanyType?.value));
		} else if (sdReportTypes?.some((type) => type?.value === reportValue)) {
			return c.insuranceCompanyTypes.filter((insuranceCompanyType) => sdCompanyTypes?.includes(insuranceCompanyType?.value));
		} else if (fileReportTypes?.some((type) => type?.value === reportValue)) {
			return c.insuranceCompanyTypes.filter((insuranceCompanyType) => fileCompanyTypes?.includes(insuranceCompanyType?.value));
		}
		return [];
	};

	return (
		<Modal closeOnEsc={true} closeOnOverlayClick={true} isOpen={true} onClose={onClose}>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader pb={0} mb={0}>
					{t('Create')}
				</ModalHeader>
				<ModalCloseButton isDisabled={false} />
				<ModalBody>
					<Formik
						initialValues={{
							reportType: reportType ?? '',
							companyType: companyType ?? '',
							company: companyId ? { companyId } : null,
							year: year ?? '',
							month: month ?? '',
							quarter: quarter ?? '',
							fileReportId: fileReportId ?? '',
							active,
						}}
						validationSchema={validationSchema}
						onSubmit={(values) => {
							let queries = '?page=1';
							if (values?.reportType) {
								queries += `&reportType=${values?.reportType}`;
								if (
									values.reportType === mdMonthlySale?.value ||
									values.reportType === mdMonthlyClaim?.value ||
									values.reportType === mdMonthlyDistribution?.value
								) {
									if (values?.month) {
										queries += `&month=${values?.month}`;
									}
								} else if (
									values.reportType === mdQuarterlySale?.value ||
									values.reportType === mdQuarterlyClaim?.value ||
									values.reportType === sdQuarterlyFinance?.value ||
									values.reportType === sdQuarterlyInvestment?.value
								) {
									if (values?.quarter) {
										queries += `&quarter=${values?.quarter}`;
									}
								} else if (
									values?.reportType === monthlyFileReport?.value ||
									values?.reportType === quarterlyFileReport?.value ||
									values?.reportType === semesterlyFileReport?.value ||
									values?.reportType === annuallyFileReport?.value
								) {
									if (values?.fileReportId) {
										queries += `&fileReportId=${values?.fileReportId}`;
									}
								}
							}
							if (values?.companyType) {
								queries += `&companyType=${values?.companyType}`;
							}
							if (values?.company?.companyId) {
								queries += `&companyId=${values?.company?.companyId}`;
							}
							if (values?.year) {
								queries += `&year=${values?.year}`;
							}
							if (values?.active === true) {
								queries += `&active=${values?.active}`;
							}
							onApply(queries);
							onClose();
						}}
					>
						{({ values, errors, props }) => {
							return (
								<Form>
									<Box mt="2">
										<>
											<SelectValueFormField
												placeholder={t('Report Type')}
												name="reportType"
												label="Report Type"
												options={reports}
												required={false}
											/>
										</>
										<>
											<CompanyTypeFormField
												{...props}
												name="companyType"
												companyFormName="company"
												label="Company Type"
												placeholder={t('Company Type')}
												types={getCompanyTypes(user, values?.reportType)}
												required={false}
											/>
										</>
										<>
											<CompanyFormField
												{...props}
												name="company"
												companyTypeFormName="companyType"
												label="Company"
												types={getCompanyTypes(user, values?.reportType)?.map((e) => e?.value)}
												required={false}
											/>
										</>
										<>
											{values?.reportType !== monthlyFileReport?.value &&
												values?.reportType !== quarterlyFileReport?.value &&
												values?.reportType !== semesterlyFileReport?.value &&
												values?.reportType !== annuallyFileReport?.value && (
													<SelectValueFormField
														{...props}
														placeholder={t('Year')}
														name="year"
														label="Year"
														options={[0].reduce((p, c) => {
															let lp = p;
															let i = 2021;
															while (i <= moment().year()) {
																lp.push({ label: lang === 'kh' ? mappingKhmerNumber(i) : i, value: i });
																i = i + 1;
															}
															return lp?.reverse();
														}, [])}
														required={false}
													/>
												)}
										</>
										<>
											{(values.reportType === mdMonthlySale?.value ||
												values.reportType === mdMonthlyClaim?.value ||
												values.reportType === mdMonthlyDistribution?.value) && (
												<SelectValueFormField
													{...props}
													placeholder={t('Month')}
													name="month"
													label="Month"
													options={[
														{ label: lang === 'kh' ? 'មករា' : 'January', value: 0 },
														{ label: lang === 'kh' ? 'កុម្ភៈ' : 'February', value: 1 },
														{ label: lang === 'kh' ? 'មីនា' : 'March', value: 2 },
														{ label: lang === 'kh' ? 'មេសា' : 'April', value: 3 },
														{ label: lang === 'kh' ? 'ឧសភា' : 'May', value: 4 },
														{ label: lang === 'kh' ? 'មិថុនា' : 'June', value: 5 },
														{ label: lang === 'kh' ? 'កក្កដា' : 'July', value: 6 },
														{ label: lang === 'kh' ? 'សីហា' : 'August', value: 7 },
														{ label: lang === 'kh' ? 'កញ្ញា' : 'September', value: 8 },
														{ label: lang === 'kh' ? 'តុលា' : 'October', value: 9 },
														{ label: lang === 'kh' ? 'វិច្ឆិកា' : 'November', value: 10 },
														{ label: lang === 'kh' ? 'ធ្នូ' : 'December', value: 11 },
													]}
													required={false}
												/>
											)}
										</>
										<>
											{(values.reportType === mdQuarterlySale?.value ||
												values.reportType === mdQuarterlyClaim?.value ||
												values.reportType === sdQuarterlyFinance?.value ||
												values.reportType === sdQuarterlyInvestment?.value) && (
												<SelectValueFormField
													{...props}
													placeholder={t('Quarter')}
													name="quarter"
													label="Quarter"
													options={[
														{ label: lang === 'kh' ? 'ត្រីមាសទី 1' : 'Quarter 1', value: 1 },
														{ label: lang === 'kh' ? 'ត្រីមាសទី 2' : 'Quarter 2', value: 2 },
														{ label: lang === 'kh' ? 'ត្រីមាសទី 3' : 'Quarter 3', value: 3 },
														{ label: lang === 'kh' ? 'ត្រីមាសទី 4' : 'Quarter 4', value: 4 },
													]}
													required={false}
												/>
											)}
										</>
										<>
											{(values?.reportType === monthlyFileReport?.value ||
												values?.reportType === quarterlyFileReport?.value ||
												values?.reportType === semesterlyFileReport?.value ||
												values?.reportType === annuallyFileReport?.value) && (
												<CompanyReportTypeFormField
													{...props}
													period={periodMapping[values?.reportType]}
													placeholder={t('Report')}
													name="fileReportId"
													label="Report"
													required={true}
												/>
											)}
										</>
										<>
											<CheckMarkFormField {...props} label="Active" name="active" />
										</>
										<>
											<Flex flex="1" mt={4} mb={2} justifyContent="flex-end" alignItems="center">
												<Button
													id="login-btn"
													variant="solid"
													size="md"
													h="42px"
													w="128px"
													type="submit"
													borderRadius={c.borderRadius}
													color="#3498db"
												>
													{t('Apply')}
												</Button>
												<Button
													size="md"
													h="42px"
													w="128px"
													onClick={() => {
														onApply('?page=1');
														onClose();
													}}
													variant="ghost"
													ml={4}
												>
													{t('Clear')}
												</Button>
											</Flex>
										</>
									</Box>
								</Form>
							);
						}}
					</Formik>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default Filter;
